import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { formatCents } from "../../../../lib/sales";
import { getLogoOrder, isPaidLogoOrder, logoOrderLineWords, logoOrderPictureUrl, swatchColour, type LogoOrderDetail, type LogoOrderRow } from "../../../../lib/logoOrders";
import { Btn, Modal, fmtBytes, fmtDateTime } from "../../../ui";
import { LogoOrderStatus } from "./LogoOrderRow";

/** One labelled fact, as the import settings draw theirs. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="crm-lab !mt-0">{label}</span>
      <div className="min-w-0 break-words text-[13px] font-semibold">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mt-5 first:mt-0">
      <div className="crm-card-t mb-2.5">{title}</div>
      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/** The logo of an order as a picture: the pack's own logo.png, on a light ground. */
function Picture({ order }: { order: LogoOrderRow }) {
  const t = useT();
  const [gone, setGone] = useState(false);
  useEffect(() => setGone(false), [order.id]);
  if (!order.hasFile || gone) return <p className="crm-mut text-[13px] font-normal">{t("sales.logoOrders.noPicture")}</p>;
  return (
    <div className="flex items-center justify-center rounded-2xl border border-black/10 bg-white p-5">
      <img src={logoOrderPictureUrl(order.id)} alt={t("sales.logoOrders.pictureAlt", { name: order.name })} className="max-h-40 w-auto max-w-full" onError={() => setGone(true)} />
    </div>
  );
}

/**
 * One order: who it is from, which logo it is, what was paid, and the ZIP of its files.
 *
 * Opened from a row, which already carries everything but the logo's facts; those come from
 * the order's own route, so the list never has to read the logos it shows.
 */
export function LogoOrderDialog({
  order,
  downloading,
  onDownload,
  onClose,
}: {
  order: LogoOrderRow;
  downloading: boolean;
  onDownload: (order: LogoOrderRow) => void;
  onClose: () => void;
}) {
  const t = useT();
  const [detail, setDetail] = useState<LogoOrderDetail | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setDetail(null);
    setFailed(false);
    getLogoOrder(order.id)
      .then((d) => live && setDetail(d))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [order.id]);

  const billing = order.billing;
  const paid = isPaidLogoOrder(order);
  const logo = detail?.logo ?? null;
  // What the order holds (the logo files, and the brand book and the business card when they were bought with them)
  const bought = logoOrderLineWords(detail?.lines);

  return (
    <Modal
      title={t("sales.logoOrders.dialogTitle", { code: order.code })}
      label={t("sales.logoOrders.dialogTitle", { code: order.code })}
      headExtra={<LogoOrderStatus order={order} />}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose}>{t("common.close")}</Btn>
          <Btn tone="dark" onClick={() => onDownload(order)} disabled={!order.hasFile || downloading}>
            {downloading ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
            {t("sales.logoOrders.download")}
          </Btn>
        </>
      }
    >
      <div className="max-h-[62vh] overflow-y-auto px-1 pb-1">
        {order.test ? <p className="crm-mut mb-4 text-[13px] leading-relaxed">{t("sales.logoOrders.testNote")}</p> : null}
        {!paid ? <p className="crm-mut mb-4 text-[13px] leading-relaxed">{t("sales.logoOrders.unpaidBody")}</p> : null}
        {paid && !order.hasFile ? <p className="crm-mut mb-4 text-[13px] leading-relaxed">{t("sales.logoOrders.noFileBody")}</p> : null}

        <Section title={t("sales.logoOrders.sectionBuyer")}>
          <Fact label={t("sales.logoOrders.billName")}>{billing?.name || "-"}</Fact>
          <Fact label={t("sales.logoOrders.email")}>
            <a href={`mailto:${order.email}`} className="hover:underline">
              {order.email}
            </a>
          </Fact>
          {billing?.regCode ? <Fact label={t("sales.logoOrders.regCode")}>{billing.regCode}</Fact> : null}
          {billing?.vatNumber ? <Fact label={t("sales.logoOrders.vatNumber")}>{billing.vatNumber}</Fact> : null}
          {billing?.address ? <Fact label={t("sales.logoOrders.address")}>{billing.address}</Fact> : null}
        </Section>

        <Section title={t("sales.logoOrders.sectionLogo")}>
          <div className="sm:col-span-2">
            <Picture order={order} />
          </div>
          <Fact label={t("sales.logoOrders.logoName")}>{logo?.name || order.name || "-"}</Fact>
          {logo?.slogan ? <Fact label={t("sales.logoOrders.slogan")}>{logo.slogan}</Fact> : null}
          {logo ? (
            <>
              <Fact label={t("sales.logoOrders.font")}>{logo.font}</Fact>
              <Fact label={t("sales.logoOrders.colours")}>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  {logo.colours.map((colour) => (
                    <span key={colour} className="crm-tnum inline-flex items-center gap-1.5">
                      <span className="size-3.5 flex-none rounded-[4px] border border-black/15" style={{ background: swatchColour(colour) ?? "transparent" }} aria-hidden />
                      {colour.toUpperCase()}
                    </span>
                  ))}
                </span>
              </Fact>
            </>
          ) : failed ? (
            <Fact label={t("sales.logoOrders.font")}>
              <span className="font-normal text-red-600">{t("sales.logoOrders.loadFailed")}</span>
            </Fact>
          ) : detail ? null : (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          )}
          {detail?.pageUrl ? (
            <Fact label={t("sales.logoOrders.page")}>
              <a href={detail.pageUrl} target="_blank" rel="noreferrer" className="font-normal hover:underline">
                {detail.pageUrl.replace(/^https?:\/\//, "")}
              </a>
            </Fact>
          ) : null}
        </Section>

        <Section title={t("sales.logoOrders.sectionPayment")}>
          {bought.length ? <Fact label={t("sales.logoOrders.bought")}>{bought.map((word) => t(word.key, word.vars)).join(", ")}</Fact> : null}
          <Fact label={t("sales.logoOrders.amount")}>
            <span className="crm-tnum">{formatCents(order.amountCents, order.currency)}</span>
          </Fact>
          <Fact label={t("sales.logoOrders.ordered")}>
            <span className="crm-tnum">{fmtDateTime(order.createdAt)}</span>
          </Fact>
          {order.paidAt ? (
            <Fact label={t("sales.logoOrders.paidAt")}>
              <span className="crm-tnum">{fmtDateTime(order.paidAt)}</span>
            </Fact>
          ) : null}
          {order.invoiceId ? (
            <Fact label={t("sales.logoOrders.invoice")}>
              <Link to={`/sales/invoices/${order.invoiceId}`} className="crm-tnum hover:underline">
                {order.invoiceNumber || t("sales.logoOrders.openInvoice")}
              </Link>
            </Fact>
          ) : null}
          {detail?.clientId ? (
            <Fact label={t("sales.logoOrders.client")}>
              <Link to={`/clients/${detail.clientId}`} className="hover:underline">
                {billing?.name || order.email}
              </Link>
            </Fact>
          ) : null}
          {order.hasFile ? (
            <Fact label={t("sales.logoOrders.file")}>
              <span className="break-all">{order.fileName}</span>
              <span className="crm-mut font-normal"> · {fmtBytes(order.fileBytes)}</span>
            </Fact>
          ) : null}
        </Section>
      </div>
    </Modal>
  );
}
